// @ts-nocheck
import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import { supabaseClient } from "@/lib/supabase/client"
import { useToast } from "@/components/ui/ToastProvider"

const QUOTES = [
  "The best investment is in the tools of one's own trade.",
  "Ideas are the currency of the future.",
  "Communities are built on shared curiosity.",
  "Every great answer starts with a great question.",
  "Knowledge shared is knowledge doubled.",
]

export default function LoginPage() {
  const navigate = useNavigate()
  const { error: showErr, success } = useToast()
  const [mode, setMode] = useState<"pass" | "otp-req" | "otp-verify">("pass")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [otpCode, setOtpCode] = useState("")
  const [showPw, setShowPw] = useState(false)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<Record<string, string>>({})
  const [quote] = useState(QUOTES[Math.floor(Math.random() * QUOTES.length)])

  const validate = () => {
    const e: Record<string, string> = {}
    if (!email || !/^[^@]+@[^@]+\.[^@]+$/.test(email)) e.email = "Enter a valid email"
    if (mode === "pass" && !password) e.password = "Password required"
    setErr(e)
    return !Object.keys(e).length
  }

  const handlePasswordLogin = async (ev: React.FormEvent) => {
    ev.preventDefault()
    if (!validate()) return
    setLoading(true)
    try {
      const { data, error } = await supabaseClient.auth.signInWithPassword({ email, password })
      if (error) { setErr({ password: error.message || "Incorrect email or password" }); return }
      if (data.session) {
        success("Welcome back!")
        navigate("/feed")
      }
    } catch (e: any) {
      setErr({ password: e.message || "Login failed" })
    } finally { setLoading(false) }
  }

  const handleOTPRequest = async (ev: React.FormEvent) => {
    ev.preventDefault()
    if (!validate()) return
    setLoading(true)
    try {
      const { error } = await supabaseClient.auth.signInWithOtp({ email, options: { shouldCreateUser: false } })
      if (error) { showErr("OTP failed", error.message); return }
      setOtpCode("")
      setMode("otp-verify")
    } catch (e: any) {
      showErr("OTP failed", e.message)
    } finally { setLoading(false) }
  }

  const handleOTPVerify = async (ev: React.FormEvent) => {
    ev.preventDefault()
    const token = otpCode.replace(/\s/g, "").trim()
    if (token.length < 6) { showErr("Invalid code", "Enter the full OTP code"); return }
    setLoading(true)
    try {
      const { data, error } = await supabaseClient.auth.verifyOtp({ email, token, type: "email" })
      if (error) { showErr("Invalid code", error.message); return }
      if (data.session) { success("Logged in!"); navigate("/feed") }
    } catch (e: any) {
      showErr("Verify failed", e.message)
    } finally { setLoading(false) }
  }

  return (
    <div className="auth-wrap">
      <div className="auth-side-left hidden lg:flex flex-col">
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: "4rem" }}>
          <SquidsIcon size={36} />
          <span className="sq-logo" style={{ fontSize: "1.75rem" }}>SQUIDS</span>
        </div>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <h2 className="font-outfit" style={{ fontWeight: 900, fontSize: "clamp(2rem,4vw,3rem)", lineHeight: 1.15, color: "var(--text)", marginBottom: "1.25rem" }}>
              Where ideas<br /><span style={{ color: "var(--brand)" }}>surface.</span>
            </h2>
            <p style={{ fontSize: ".95rem", color: "var(--text2)", lineHeight: 1.7, marginBottom: "2.5rem" }}>
              Africa's platform for questions, ideas, and real conversation.
            </p>
            <blockquote style={{ borderLeft: "2px solid var(--brand)", paddingLeft: "1rem", fontStyle: "italic", fontSize: ".85rem", color: "var(--text2)" }}>
              "{quote}"
            </blockquote>
          </motion.div>
        </div>
        <div style={{ display: "flex", gap: "1rem" }}>
          {["Terms", "Privacy", "Help"].map(l => (
            <a key={l} href="https://policies.squids.co.za" target="_blank" rel="noreferrer"
              style={{ fontSize: ".75rem", color: "var(--text3)" }}
              className="hover:text-brand transition-colors">{l}</a>
          ))}
        </div>
        <FloatingOrb />
      </div>

      <div className="auth-side-right" style={{ flex: 1 }}>
        <motion.div className="auth-box" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: "1.5rem" }} className="lg:hidden">
            <SquidsIcon size={28} /><span className="sq-logo" style={{ fontSize: "1.4rem" }}>SQUIDS</span>
          </div>

          <AnimatePresence mode="wait">
            {mode === "pass" && (
              <motion.div key="pass" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }}>
                <h1 className="font-outfit" style={{ fontWeight: 700, fontSize: "1.2rem", marginBottom: ".25rem" }}>Log in to SQUIDS</h1>
                <p style={{ fontSize: ".85rem", color: "var(--text2)", marginBottom: "1.5rem" }}>Good to have you back.</p>
                <form onSubmit={handlePasswordLogin} style={{ display: "flex", flexDirection: "column", gap: ".75rem" }}>
                  <div>
                    <input type="email" value={email} onChange={e => { setEmail(e.target.value); setErr({}) }}
                      placeholder="Email address" className="sq-input" autoComplete="email" autoFocus />
                    {err.email && <p style={{ fontSize: ".72rem", color: "var(--danger)", marginTop: ".25rem" }}>{err.email}</p>}
                  </div>
                  <div>
                    <div style={{ position: "relative" }}>
                      <input type={showPw ? "text" : "password"} value={password}
                        onChange={e => { setPassword(e.target.value); setErr({}) }}
                        placeholder="Password" className="sq-input" style={{ paddingRight: "3.5rem" }} autoComplete="current-password" />
                      <button type="button" onClick={() => setShowPw(v => !v)}
                        style={{ position: "absolute", right: ".75rem", top: "50%", transform: "translateY(-50%)", fontSize: ".7rem", fontWeight: 700, color: "var(--text3)", background: "none", border: "none", cursor: "pointer" }}>
                        {showPw ? "HIDE" : "SHOW"}
                      </button>
                    </div>
                    {err.password && <p style={{ fontSize: ".72rem", color: "var(--danger)", marginTop: ".25rem" }}>{err.password}</p>}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Link to="/auth/forgot-password" style={{ fontSize: ".75rem", fontWeight: 600, color: "var(--brand)" }}>Forgot password?</Link>
                    <button type="button" onClick={() => setMode("otp-req")} style={{ fontSize: ".75rem", fontWeight: 600, color: "var(--text2)", background: "none", border: "none", cursor: "pointer" }}>Use OTP instead</button>
                  </div>
                  <button type="submit" disabled={loading} className="sq-btn sq-btn-primary" style={{ width: "100%", padding: ".6rem", justifyContent: "center" }}>
                    {loading ? <Spinner /> : "Log In"}
                  </button>
                </form>
                <div className="sq-divider" style={{ margin: "1rem 0" }}>
                  <span style={{ fontSize: ".72rem", color: "var(--text3)" }}>or</span>
                </div>
                <Link to="/auth/register" className="sq-btn sq-btn-outline" style={{ width: "100%", padding: ".6rem", justifyContent: "center", display: "flex" }}>Create Account</Link>
              </motion.div>
            )}

            {mode === "otp-req" && (
              <motion.div key="otp-req" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }}>
                <button onClick={() => setMode("pass")} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: ".85rem", color: "var(--text2)", background: "none", border: "none", cursor: "pointer", marginBottom: "1rem" }}>
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                  Back
                </button>
                <h1 className="font-outfit" style={{ fontWeight: 700, fontSize: "1.2rem", marginBottom: ".25rem" }}>Log in with OTP</h1>
                <p style={{ fontSize: ".85rem", color: "var(--text2)", marginBottom: "1.5rem" }}>We'll send a one-time code to your email.</p>
                <form onSubmit={handleOTPRequest} style={{ display: "flex", flexDirection: "column", gap: ".75rem" }}>
                  <div>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email address" className="sq-input" autoFocus autoComplete="email" />
                    {err.email && <p style={{ fontSize: ".72rem", color: "var(--danger)", marginTop: ".25rem" }}>{err.email}</p>}
                  </div>
                  <button type="submit" disabled={loading} className="sq-btn sq-btn-primary" style={{ width: "100%", padding: ".6rem", justifyContent: "center" }}>
                    {loading ? <Spinner /> : "Send Code"}
                  </button>
                </form>
              </motion.div>
            )}

            {mode === "otp-verify" && (
              <motion.div key="otp-verify" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }}>
                <button onClick={() => setMode("otp-req")} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: ".85rem", color: "var(--text2)", background: "none", border: "none", cursor: "pointer", marginBottom: "1rem" }}>
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                  Back
                </button>
                <h1 className="font-outfit" style={{ fontWeight: 700, fontSize: "1.2rem", marginBottom: ".25rem" }}>Enter your code</h1>
                <p style={{ fontSize: ".85rem", color: "var(--text2)", marginBottom: ".5rem" }}>Sent to <strong>{email}</strong></p>
                <p style={{ fontSize: ".75rem", color: "var(--text3)", marginBottom: "1.25rem" }}>Check your inbox — the code is 6 or 8 digits.</p>
                <form onSubmit={handleOTPVerify} style={{ display: "flex", flexDirection: "column", gap: ".75rem" }}>
                  <div>
                    <label style={{ fontSize: ".7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", color: "var(--text3)", display: "block", marginBottom: ".4rem" }}>
                      Enter OTP code
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={otpCode}
                      onChange={e => setOtpCode(e.target.value.replace(/[^0-9]/g, ""))}
                      placeholder="e.g. 123456 or 12345678"
                      className="sq-input"
                      maxLength={8}
                      autoFocus
                      autoComplete="one-time-code"
                      style={{ letterSpacing: ".2em", fontSize: "1.1rem", textAlign: "center" }}
                    />
                    <p style={{ fontSize: ".7rem", color: "var(--text3)", marginTop: ".3rem" }}>Paste or type your 6 or 8 digit code</p>
                  </div>
                  <button type="submit" disabled={loading || otpCode.length < 6} className="sq-btn sq-btn-primary" style={{ width: "100%", padding: ".6rem", justifyContent: "center" }}>
                    {loading ? <Spinner /> : "Verify & Log In"}
                  </button>
                </form>
                <button onClick={() => setMode("otp-req")} style={{ marginTop: "1rem", width: "100%", textAlign: "center", fontSize: ".72rem", color: "var(--text3)", background: "none", border: "none", cursor: "pointer" }}>
                  Resend code
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          <p style={{ textAlign: "center", fontSize: ".72rem", color: "var(--text3)", marginTop: "1rem" }}>
            By continuing you agree to our{" "}
            <a href="https://policies.squids.co.za" target="_blank" rel="noreferrer" style={{ color: "var(--brand)" }}>Terms</a>
            {" "}&amp;{" "}
            <a href="https://policies.squids.co.za" target="_blank" rel="noreferrer" style={{ color: "var(--brand)" }}>Privacy</a>
          </p>
        </motion.div>
      </div>
    </div>
  )
}

function SquidsIcon({ size = 32 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <rect width="48" height="48" rx="10" fill="#ff4500" />
      <path d="M15 17.5C15 14.462 17.462 12 20.5 12H29C30.105 12 31 12.895 31 14C31 15.105 30.105 16 29 16H20.5C19.672 16 19 16.672 19 17.5C19 18.328 19.672 19 20.5 19H27.5C30.538 19 33 21.462 33 24.5C33 27.538 30.538 30 27.5 30H19C17.895 30 17 30.895 17 32C17 33.105 17.895 34 19 34H28.5C29.328 34 30 34.672 30 35.5C30 36.328 29.328 37 28.5 37H19C16.239 37 14 34.761 14 32C14 29.239 16.239 27 19 27H27.5C28.328 27 29 26.328 29 25.5C29 24.672 28.328 24 27.5 24H20.5C17.462 24 15 21.538 15 18.5V17.5Z" fill="white" />
    </svg>
  )
}

function FloatingOrb() {
  return (
    <div style={{ position: "absolute", top: "30%", right: "-60px", width: "240px", height: "240px", borderRadius: "9999px", background: "radial-gradient(circle,rgba(255,69,0,.15) 0%,transparent 70%)", pointerEvents: "none", animation: "float 8s ease-in-out infinite" }} />
  )
}

function Spinner() {
  return (
    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity=".25" />
      <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  )
}
